import { NextSeo } from 'next-seo';
import React from 'react';
import Container from '../components/Container';
import PageHeader from '../components/PageHeader';
import config from '../config';

function PrivacyPolicyPage() {
  const description = `Learn how Shitty Kitties and SK Club collect, use, and protect your data.`;

  return (
    <>
      <NextSeo title={`Privacy Policy - ${config.siteName}`} description={description} />

      <div className='pb-12 md:pb-48 bg-bg'>
        <PageHeader heading={'Privacy Policy'} description={description} />
        <Container className='mx-auto'>
          <p className='text-sm text-gray-400 mb-6'>Effective Date: April 10, 2026</p>

          <div className='prose'>
            <h2>Introduction</h2>
            <p>
              Welcome to Shitty Kitties and SK Club ("we," "our," "us"). We are committed to protecting your privacy and ensuring transparency with
              respect to how we collect, use, and share information about our users. This Privacy Policy describes how we manage personal information
              collected through <a href='https://www.shittykitties.art'>https://www.shittykitties.art</a> (the "Site") and our SK Club mobile
              applications, including Frik'd Up Fishies (collectively, the "Apps"), together referred to as our "Services." It also outlines our
              responsibilities under data privacy regulations and your rights as a user.
            </p>

            <h2>1. Information We Collect</h2>

            <h3>Website (shittykitties.art)</h3>
            <p>
              <strong>Personal Information:</strong> When you interact with our Site, we may collect personal information such as your email and
              wallet address if you choose to register or connect your Algorand wallet and Discord account.
            </p>
            <p>
              <strong>Usage Data:</strong> We collect information about your interactions with the Site, such as pages visited, time spent on each
              page, actions performed, and referring URLs. This data is collected through cookies and similar tracking technologies to improve and
              personalize your experience.
            </p>
            <p>
              <strong>Device Information:</strong> We may collect device information, such as IP address, browser type, operating system, and mobile
              device identifiers.
            </p>

            <h3>Mobile Applications (SK Club)</h3>
            <p>
              <strong>Authentication Data:</strong> Our Apps support anonymous/guest authentication via Firebase Auth. If you choose to create an
              account or sign in, we may collect your email address or other authentication credentials.
            </p>
            <p>
              <strong>Game Data:</strong> We collect gameplay information such as game progress, fish collection data, virtual currency balances, and
              achievement data. This data is stored locally on your device using MMKV and may be synced to Firebase for cloud backup and
              cross-device access.
            </p>
            <p>
              <strong>Device Identifiers:</strong> We collect device identifiers and device-level information such as device model, operating system
              version, and unique device identifiers to provide and improve our Services.
            </p>
            <p>
              <strong>App Usage and Session Data:</strong> We collect information about how you use our Apps, including session duration, features
              used, screens visited, and in-app actions to help us improve the user experience.
            </p>
            <p>
              <strong>In-App Purchase History:</strong> We collect records of in-app purchases and subscription data processed through RevenueCat and
              the Apple App Store or Google Play Store.
            </p>
            <p>
              <strong>Push Notification Tokens:</strong> If you opt in to push notifications, we collect your device's push notification token to
              deliver notifications.
            </p>
            <p>
              <strong>Analytics Data:</strong> We collect analytics data through PostHog, including user interactions, feature usage, and performance
              metrics.
            </p>
            <p>
              <strong>Crash and Error Reports:</strong> We collect crash logs and error reports through Sentry to identify and fix technical issues.
            </p>

            <h2>2. How We Use Your Information</h2>
            <p>We use your information for the following purposes:</p>
            <ul>
              <li>
                <strong>Service Provisioning:</strong> To provide and improve the features of our Services, including NFT interactions on the Site,
                mobile gameplay, and related utilities.
              </li>
              <li>
                <strong>Analytics and Personalization:</strong> To analyze user behavior, improve performance, and personalize your experience based
                on previous interactions.
              </li>
              <li>
                <strong>Purchase Management:</strong> To process and manage in-app purchases and subscriptions through our mobile Apps.
              </li>
              <li>
                <strong>Security and Compliance:</strong> To detect, prevent, and address fraud, unauthorized access, and any security or technical
                issues.
              </li>
              <li>
                <strong>Bug Fixing and Stability:</strong> To monitor app stability, diagnose crashes, and resolve errors using data collected
                through Sentry.
              </li>
            </ul>

            <h2>3. Third-Party Services and SDKs</h2>
            <p>Our Services use the following third-party services that may collect and process data:</p>

            <h3>Website</h3>
            <ul>
              <li>
                <strong>Firebase:</strong> Authentication and cloud data storage.
              </li>
            </ul>

            <h3>Mobile Applications</h3>
            <ul>
              <li>
                <strong>PostHog:</strong> Analytics and product insights, including user behavior tracking and feature usage analysis.
              </li>
              <li>
                <strong>Sentry:</strong> Error tracking and crash reporting to help us identify and fix bugs.
              </li>
              <li>
                <strong>RevenueCat:</strong> In-app purchase and subscription management, including purchase validation and subscription status
                tracking.
              </li>
              <li>
                <strong>Firebase:</strong> Authentication (including anonymous/guest login) and cloud data storage for game progress syncing.
              </li>
              <li>
                <strong>Expo:</strong> App delivery, over-the-air updates, and push notifications.
              </li>
            </ul>
            <p>
              Each of these third-party services has its own privacy policy governing how they collect and use data. We encourage you to review their
              respective privacy policies.
            </p>

            <h2>4. Sharing Your Information</h2>
            <p>
              We do not sell or rent your personal information to third parties. We may, however, share your information in the following
              circumstances:
            </p>
            <ul>
              <li>
                <strong>Service Providers:</strong> With trusted third-party providers who perform services on our behalf (e.g., hosting, analytics,
                payment processing, error tracking) to help us operate our Services effectively.
              </li>
              <li>
                <strong>Legal Obligations:</strong> When required by law or to comply with legal processes, such as responding to a subpoena or
                regulatory inquiry.
              </li>
              <li>
                <strong>Business Transactions:</strong> If our business undergoes a merger, acquisition, or other structural changes, your information
                may be transferred as part of that transaction.
              </li>
            </ul>

            <h2>5. Cookies, Local Storage, and Tracking Technologies</h2>
            <p>
              <strong>Website:</strong> The Site uses cookies and similar tracking technologies to personalize your experience, remember your
              preferences, and analyze usage data. You can control cookie preferences through your browser settings. Note that disabling cookies may
              impact the Site's functionality.
            </p>
            <p>
              <strong>Mobile Applications:</strong> Our Apps use local storage (MMKV) rather than cookies to store game data, preferences, and
              session information on your device. Analytics and tracking in the Apps are handled through the third-party SDKs described in Section 3.
            </p>

            <h2>6. Security</h2>
            <p>
              We implement industry-standard security measures to protect your information from unauthorized access, disclosure, alteration, or
              destruction. However, please remember that no method of transmission over the internet or electronic storage is 100% secure, and we
              cannot guarantee absolute security.
            </p>

            <h2>7. Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a
              longer retention period is required by law. Game data stored locally on your device persists until you delete the App or clear its data.
            </p>

            <h2>8. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have rights concerning your personal information, including:</p>
            <ul>
              <li>
                <strong>Access and Correction:</strong> Request access to or correction of your personal information held by us.
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of personal information we have about you, subject to legal or contractual restrictions.
              </li>
              <li>
                <strong>Opt-Out:</strong> Choose not to receive marketing communications or push notifications from us.
              </li>
              <li>
                <strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format.
              </li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at <a href='mailto:support@minnerlabs.com'>support@minnerlabs.com</a>.
            </p>

            <h2>9. Third-Party Links</h2>
            <p>
              Our Services may contain links to third-party websites or services. This Privacy Policy applies solely to information collected through
              our Services, and we are not responsible for the privacy practices or content of third-party sites or services. We encourage you to
              review the privacy policies of any third-party sites you visit.
            </p>

            <h2>10. Children's Privacy</h2>
            <p>
              Our Services are not directed at children under the age of 13. We do not knowingly collect personal information from children under 13.
              If we become aware that we have collected personal information from a child under 13, we will take steps to delete that information
              promptly. If you believe a child under 13 has provided us with personal information, please contact us at{' '}
              <a href='mailto:support@minnerlabs.com'>support@minnerlabs.com</a>.
            </p>

            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or other factors.
              When we make changes, we will update the "Effective Date" and notify users as appropriate. Your continued use of our Services after such
              changes signifies your acceptance of the revised Privacy Policy.
            </p>

            <h2>12. Contact Us</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please reach out to us at:</p>
            <p>
              <strong>Email:</strong> <a href='mailto:support@minnerlabs.com'>support@minnerlabs.com</a>
            </p>

            <p>
              By using our Services, including <a href='https://www.shittykitties.art'>https://www.shittykitties.art</a> and our SK Club mobile
              applications, you acknowledge that you have read, understood, and agree to this Privacy Policy.
            </p>
          </div>
        </Container>
      </div>
    </>
  );
}

export default PrivacyPolicyPage;
